import { EventHealthApiIssue, EventHealthFindingDetails, EventHealthIssue } from '../../tools-reports/event-health/event-health.types';
import { mapEventHealthGuidance, EventHealthGuidance } from './event-health-guidance.mapper';
import { HealthGuidanceOptions, normalizeHealthScope, normalizeHealthSeverity } from './event-health-targets';
import { HealthResolution, HealthReviewFinding } from './event-health-form-review.types';

export interface MappedEventHealthIssue {
  issue: EventHealthIssue;
  guidance: EventHealthGuidance & { resolution: HealthResolution };
}

/** One adapter for the report dialog and the in-form health assistant. */
export function mapEventHealthIssue(
  eventType: string,
  source: EventHealthApiIssue,
  options: HealthGuidanceOptions = {},
): MappedEventHealthIssue {
  const details = mergeDetails(source);
  const normalizedSource: EventHealthApiIssue = { ...source, ...details };
  const mappedGuidance = mapEventHealthGuidance(eventType, normalizedSource, options);
  const guidance = {
    ...mappedGuidance,
    resolution: resolveHealthResolution(normalizedSource, mappedGuidance.target),
  };

  return {
    guidance,
    issue: {
      ruleId: source.code,
      severity: normalizeHealthSeverity(source.severity),
      scope: normalizeHealthScope(source.category),
      title: guidance.title || source.title?.trim() || 'Event information needs attention',
      message: source.description,
      help: guidance.help,
      field: source.field,
      path: source.path,
      ...details,
    },
  };
}

/**
 * Classifies how a finding can be resolved without changing the backend contract.
 * Keep this decision in the mapping layer so UI consumers do not infer recovery
 * behavior from focus/navigation fields independently.
 */
export function resolveHealthResolution(
  issue: EventHealthApiIssue,
  target: HealthReviewFinding['target'],
): HealthResolution {
  if (issue.reason?.startsWith('master-data-') || normalizeHealthScope(issue.category) === 'master-data') {
    return { group: 'master-data', mechanism: 'resolve-master-data' };
  }
  if (target.generatedOnSave) {
    return { group: 'automatic', mechanism: 'generated-on-save' };
  }
  if (target.managedAutomatically) {
    return { group: 'automatic', mechanism: 'managed-automatically' };
  }
  if (target.unavailableReason) {
    return { group: 'support', mechanism: 'contact-support' };
  }
  if (target.controlName || target.field || target.selector || target.collection) {
    return { group: 'form', mechanism: 'edit-form' };
  }
  return { group: 'support', mechanism: 'contact-support' };
}

function mergeDetails(source: EventHealthApiIssue): EventHealthFindingDetails {
  const details = source.details ?? {};
  return {
    label: source.label ?? details.label,
    reason: source.reason ?? details.reason,
    requiredCertificateType: source.requiredCertificateType ?? details.requiredCertificateType,
    missingPaths: copy(source.missingPaths ?? details.missingPaths),
    entityType: source.entityType ?? details.entityType,
    reference: source.reference ?? details.reference,
    actualValue: source.actualValue ?? details.actualValue,
    expectedValue: source.expectedValue ?? details.expectedValue,
    allowedValues: copy(source.allowedValues ?? details.allowedValues),
    constraint: source.constraint ?? details.constraint,
    schemaViolations: (source.schemaViolations ?? details.schemaViolations)?.map((item) => ({ ...item })),
    duplicates: (source.duplicates ?? details.duplicates)?.map((item) => ({ ...item, indexes: [...item.indexes] })),
  };
}

function copy<T>(value: T[] | null | undefined): T[] | null | undefined {
  return value ? [...value] : value;
}
